import { addCleanPageAction } from '@src/utils/newrelic';

/**
 * New Relic PageAction instrumentation for the Self-Service Returns flow —
 * Phase 2 (MYACCR-2592), mirroring the Orders & Purchases repo.
 *
 * Four event types capture what NR's auto-captured AjaxRequest/JavaScriptError/PageView
 * events can't express: the wizard funnel (a single-route Zustand step machine),
 * coded business rejections, business outcomes hidden behind 2xx responses, and
 * silent UI-suppression gates.
 *
 * PII guardrail (hard rule): the exported emitters accept ONLY the whitelisted
 * attributes declared in the interfaces below. Never widen these to carry address,
 * name, phone, email, guest token, or item descriptions. Identifiers that are not
 * PII — return/order ids, and the userId already present in every request URL —
 * are allowed where they earn their place by making an event actionable.
 */

export type ReturnsFlowStepName =
  | 'eligibility_loaded'
  | 'items_selected'
  | 'qty_reason_submitted'
  | 'method_selected'
  | 'boxes_selected'
  | 'lorp_scheduled'
  | 'refund_method_selected'
  | 'keep_it_selected'
  | 'review_viewed'
  | 'return_created'
  | 'summary_viewed'
  | 'flow_abandoned'
  | 'error_page_viewed';

export type ReturnsResult = 'success' | 'error' | 'abandoned';

/** Outcome steps whose *business* result is invisible to AjaxRequest (2xx-but-failed,
 *  timeout-unknown, duplicate-as-success, refund hidden, QR generated, partial). */
export type ReturnsOutcomeStep =
  | 'create_return'
  | 'keep_it_create'
  | 'apriss_finalize'
  | 'soris_create'
  | 'print_label'
  | 'refund_estimation';

export type ReturnsOutcomeResult =
  | 'success'
  | 'error'
  | 'business_fail'
  | 'timeout'
  | 'duplicate'
  | 'partial'
  | 'hidden'
  | 'shown'
  | 'qr_generated'
  | 'abandoned'
  | 'skipped';

export type ReturnsGate = 'return_method' | 'auth_redirect';

export type ReturnsGateResultValue =
  | 'in_store_only'
  | 'cash_and_carry'
  | 'redirect';

/** Shared, non-PII context stamped on every returns PageAction. */
export interface ReturnsContext {
  /** Stable per-session correlation id (SESSION_CORRELATION_ID) to join to AjaxRequest. */
  correlationId: string;
  returnMethod?: string;
  /**
   * Sorted, '+'-joined fulfillment mix of the selected items (e.g. 'STH+BOSS'), so the
   * dashboard can break returns down by fulfillment the same way it does by method.
   */
  fulfillmentType?: string;
  itemCount?: number;
  isKeepIt?: boolean;
  militaryExchange?: string;
}

export interface ReturnsFlowStepEvent extends ReturnsContext {
  step: ReturnsFlowStepName;
  result?: ReturnsResult;
}

export interface ReturnsBusinessErrorEvent extends ReturnsContext {
  errorCode: string;
  /** Which step/API surfaced the rejection (e.g. 'policy_validator', 'validate_address'). */
  source?: string;
}

export interface ReturnsOutcomeEvent extends ReturnsContext {
  step: ReturnsOutcomeStep;
  result: ReturnsOutcomeResult;
  /**
   * Which branch produced this outcome, for outcomes a status code can't explain
   * (e.g. why a fraud finalize was skipped). MUST be a fixed literal authored here —
   * never an error message, response body, or anything derived from user/order data.
   * `addCleanPageAction` only drops empty keys; it does not scrub PII.
   */
  reason?: string;
  /**
   * The return this outcome belongs to, so a skipped/failed fraud finalize can be
   * joined to the backend record instead of only being counted. Set by the
   * per-leg reports, which concern exactly one created return.
   */
  returnOrderId?: string;
  /**
   * The same join, for outcomes that span a whole multi-method return rather than
   * one leg — the single gated report skips, which have several created returns
   * behind them or none. Read it alongside `returnOrderId` rather than instead of
   * it.
   *
   * Comma-joined, so match on the delimiters rather than a bare substring: `%X%`
   * also matches `X2`. Either compare against the whole value or bracket the term
   * with commas.
   */
  returnOrderIds?: string;
  /** Elapsed ms for the operation, where measured. */
  durationMs?: number;
  shipmentIndex?: number;
  totalShipments?: number;
  retryAttempted?: boolean;
}

export interface ReturnsGateEvent extends ReturnsContext {
  gate: ReturnsGate;
  result: ReturnsGateResultValue;
  reason?: string;
}

export const RETURNS_PAGE_ACTIONS = {
  flowStep: 'returnsFlowStep',
  businessError: 'returnsBusinessError',
  outcome: 'returnsOutcome',
  gateResult: 'returnsGateResult',
} as const;

export const trackReturnsFlowStep = (event: ReturnsFlowStepEvent): void =>
  addCleanPageAction(RETURNS_PAGE_ACTIONS.flowStep, event);

export const trackReturnsBusinessError = (
  event: ReturnsBusinessErrorEvent,
): void => addCleanPageAction(RETURNS_PAGE_ACTIONS.businessError, event);

export const trackReturnsOutcome = (event: ReturnsOutcomeEvent): void =>
  addCleanPageAction(RETURNS_PAGE_ACTIONS.outcome, event);

export const trackReturnsGateResult = (event: ReturnsGateEvent): void =>
  addCleanPageAction(RETURNS_PAGE_ACTIONS.gateResult, event);
